import Vue from 'vue';
import { i18n } from '@/plugins/i18n';
import * as actionsTypes from './action-types';
import * as mutationsTypes from './mutation-types';


export default {

  /** Get company issues */
  async [actionsTypes.FETCH_JIRA_ISSUES](store) {
    store.commit(mutationsTypes.GET_JIRA_ISSUES_LOADING, { loading: true });
    return Vue.axios({
      method: 'get',
      url: '/server/jiraIssues/get.php',
    })
      .then((response) => {
        const { data } = response;
        if (data instanceof Array && data.length) {
          const issuesFormatted = data.map((issue) => {
            // Format all dates we need to display and sort
            issue.fields.calculatedCreatedDate = Vue.moment(issue.fields.created).format('YYYYMMDDHHmmss');
            issue.fields.formattedCreatedDate = Vue.moment(issue.fields.created).format(i18n.t('dateFormat'));

            /**
             *
             * Add a status ID to filter them
             * ID 10000 = pending
             * ID 3 = on going
             * ID 10101 = finished
             * ID 6 = closed (finished)
             */
            switch (Number(issue.fields.status.id)) {
              case 10000:
                issue.statusId = 0;
                break;
              case 3:
                issue.statusId = 1;
                break;
              case 10101:
                issue.statusId = 2;
                break;
              case 6:
                issue.statusId = 2;
                break;
              default:
                issue.statusId = 0;
            }
            if (issue.fields.labels.includes('Refused')) {
              issue.statusId = 3;
            }

            // Format description
            if (issue.fields.customfield_10603) {
              issue.description = issue.fields.customfield_10603.replace(/\\/g, '');
            }

            // Format Author
            issue.author = issue.fields.customfield_10604;

            return issue;
          });
          store.commit(mutationsTypes.GET_JIRA_ISSUES, { issuesFormatted });
          store.commit(mutationsTypes.GET_JIRA_ISSUES_LOADING, { loading: false });
          return data;
        }
        throw response;
      })
      .catch(() => {
        store.commit(mutationsTypes.GET_JIRA_ISSUES_LOADING, { loading: false });
      });
  },

  /** Get a specific Jira issue (the one that was selected by the user) */
  async [actionsTypes.FETCH_JIRA_ISSUE](store, payload) {
    store.commit(mutationsTypes.GET_JIRA_ISSUE_LOADING, { ...payload, loading: true });
    return Vue.axios({
      method: 'post',
      url: '/server/jiraIssues/getIssueDetails.php',
      data: { ...payload, content: 'issue' },
    })
      .then((response) => {
        const { data } = response;
        if (data instanceof Object) {
          // Format the date
          data.fields.formattedCreatedDate = Vue.moment(data.fields.created).format(i18n.t('dateFormat'));

          /**
           *
           * Add a status ID to filter them
           * ID 10000 = pending
           * ID 3 = on going
           * ID 10101 = finished
           * ID 6 = closed (finished)
           */
          switch (Number(data.fields.status.id)) {
            case 10000:
              data.statusId = 0;
              break;
            case 3:
              data.statusId = 1;
              break;
            case 10101:
              data.statusId = 2;
              break;
            case 6:
              data.statusId = 2;
              break;
            default:
              data.statusId = 0;
          }
          if (data.fields.labels.includes('Refused')) {
            data.statusId = 3;
          }

          // Format description
          if (data.fields.customfield_10603) {
            data.description = data.fields.customfield_10603.replace(/\\/g, '');
          }

          // Format Author
          data.author = data.fields.customfield_10604;

          data.commentsLoading = true;
          data.attachmentsLoading = true;

          store.commit(mutationsTypes.GET_JIRA_ISSUE, { ...payload, data });
          store.commit(mutationsTypes.GET_JIRA_ISSUE_LOADING, { ...payload, loading: false });
          return data;
        }
        throw response;
      })
  },

  /** Get the current Jira issue attachments */
  async [actionsTypes.FETCH_JIRA_ISSUE_ATTACHMENTS](store, payload) {
    store.commit(mutationsTypes.GET_JIRA_ISSUE_ATTACHMENTS_LOADING, { ...payload, loading: true });
    return Vue.axios({
      method: 'post',
      url: '/server/jiraIssues/getIssueDetails.php',
      data: { ...payload, content: 'attachments' },
    })
      .then((response) => {
        const { data } = response;
        if (data instanceof Object) {
          store.commit(mutationsTypes.GET_JIRA_ISSUE_ATTACHMENTS_LOADING, { ...payload, loading: false });
          store.commit(mutationsTypes.GET_JIRA_ISSUE_ATTACHMENTS, { ...payload, data });
          return data;
        }
        throw response;
      })
      .catch((err) => {
        store.commit(mutationsTypes.GET_JIRA_ISSUE_ATTACHMENTS_LOADING, { ...payload, loading: false });
        throw err;
      });
  },

  /** Get the current Jira issue comments */
  async [actionsTypes.FETCH_JIRA_ISSUE_COMMENTS](store, payload) {
    store.commit(mutationsTypes.GET_JIRA_ISSUE_COMMENTS_LOADING, { ...payload, loading: true });
    return Vue.axios({
      method: 'post',
      url: '/server/jiraIssues/getIssueDetails.php',
      data: { ...payload, content: 'comments' },
    })
      .then((response) => {
        const { data } = response;
        if (data instanceof Object) {
          store.commit(mutationsTypes.GET_JIRA_ISSUE_COMMENTS_LOADING, { ...payload, loading: false });
          store.commit(mutationsTypes.GET_JIRA_ISSUE_COMMENTS, { ...payload, data });
          return data;
        }
        throw response;
      })
      .catch((err) => {
        store.commit(mutationsTypes.GET_JIRA_ISSUE_COMMENTS_LOADING, { ...payload, loading: false });
        throw err;
      });
  },

  /** Add an attachment to a Jira issue */
  async [actionsTypes.ADD_JIRA_ISSUE_ATTACHMENT](store, payload) {
    store.commit(mutationsTypes.GET_JIRA_ISSUE_ATTACHMENTS_LOADING, { ...payload, loading: true });
    return Vue.axios({
      method: 'post',
      url: '/server/jiraIssues/addAttachment.php',
      data: payload,
    })
      .then((response) => {
        const { data } = response;
        if (data instanceof Object) {
          store.commit(mutationsTypes.GET_JIRA_ISSUE_ATTACHMENTS, { ...payload, data });
          store.commit(mutationsTypes.GET_JIRA_ISSUE_ATTACHMENTS_LOADING, { ...payload, loading: false });
          return data;
        }
        throw response;
      });
  },

  /** Delete an attachment from a Jira issue */
  async [actionsTypes.DELETE_JIRA_ISSUE_ATTACHMENT](store, payload) {
    store.commit(mutationsTypes.GET_JIRA_ISSUE_ATTACHMENTS_LOADING, { ...payload, loading: true });
    return Vue.axios({
      method: 'post',
      url: '/server/jiraIssues/deleteAttachment.php',
      data: payload,
    })
      .then((response) => {
        const { data } = response;
        if (data instanceof Object) {
          store.commit(mutationsTypes.GET_JIRA_ISSUE_ATTACHMENTS, { ...payload, data });
          store.commit(mutationsTypes.GET_JIRA_ISSUE_ATTACHMENTS_LOADING, { ...payload, loading: false });
          return data;
        }
        throw response;
      });
  },
};
